import { Spacing } from '@persona-id/design-tokens';
import { ArrowLeftIcon, XIcon } from '@persona-id/design-tokens/icons';
import Fuse from 'fuse.js';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled, { keyframes } from 'styled-components';

import TemplateModuleCard from 'dashboard/apps/InquiryTemplateEditor/TemplateModuleCard';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { ModalContainer } from 'dashboard/layouts/ModalLayout';
import {
  BuilderName,
  BuilderPreviewMap,
  BuilderPreviewSteps,
  InquiryTemplateBuilder,
} from 'dashboard/models/inquiry-template';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import CreateInquiryTemplatePage from 'dashboard/views/DashboardInquiryTemplatesView/CreateInquiryTemplatePage';
import { VerificationTypeIcon } from 'dashboard/views/DashboardVerificationTemplatesView/CreateVerificationTemplateModal';
import { TYPE_FILTER_OPTIONS } from 'dashboard/views/DashboardVerificationTemplatesView/DashboardVerificationTemplatesView';
import { Heading, Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import useDebounced from 'lib/hooks/useDebounced';

const CARD_HEIGHT = 156;
const HEADER_EXPANDED = 136;
const HEADER_COLLAPSED = 72;

export const BUILDER_LABELS: Record<BuilderName, { title: string; description: string }> = {
  [BuilderName.Aamva]: {
    title: 'AAMVA',
    // Aamva verifications are expensive, so in dynamic flow they should always have some sort of govID step before it to ensure it's necessary.
    // This template builder is generally used by people manually reviewing Cases, NOT end users. Can be removed when Verifications can be created in Dashboard.
    description:
      "Use this template as a starting point for AAMVA driver's license data verifications. Standalone AAMVA verifications are recommended for internal Cases use only.",
  },
  [BuilderName.Database]: {
    title: 'Database (name, birthdate, address)',
    description:
      'Use this template as a starting point for Database identity verifications using name, birthdate, and address.',
  },
  [BuilderName.DatabaseOrGovernmentId]: {
    title: 'Database Or Government ID (Front side only)',
    description:
      'Use this template as a starting point for Database or Government ID identity verifications based on the country.',
  },
  [BuilderName.DatabaseOrGovernmentIdAndSelfie]: {
    title: 'Database Or Government ID (Front side only) and Selfie',
    description:
      'Use this template as a starting point for Database or Government ID and live Selfie identity verifications based on the country.',
  },
  [BuilderName.DatabasePhoneCarrier]: {
    title: 'Database Phone Carrier',
    description:
      'Use this template as a starting point for Database Phone Carrier verifications in the United States.',
  },
  [BuilderName.DatabaseWithSsn]: {
    title: 'Database (name, birthdate, address, social security number)',
    description:
      'Use this template as a starting point for Database identity verifications using name, birthdate, address and social security number.',
  },
  [BuilderName.Document]: {
    title: 'Document',
    description:
      'Use this template as a starting point to process non-government-issued documents. Additional configuration required.',
  },
  [BuilderName.EmailAddress]: {
    title: 'Email Address',
    description: 'Use this template as a starting point for Email Address verifications.',
  },
  [BuilderName.GovernmentId]: {
    title: 'Government ID (Front side only)',
    description:
      'Use this template as a starting point for Government ID front side identity verifications.',
  },
  [BuilderName.GovernmentIdAndProofOfAddress]: {
    title: 'Government ID and Proof of Address',
    description:
      'Use this template to verify a government-issued ID along with proof of address documentation. No additional configuration required.',
  },
  [BuilderName.GovernmentIdAndDatabase]: {
    title: 'Government ID (Front side only) and Database',
    description:
      'Use this template as a starting point for Government ID front side and Database identity verifications.',
  },
  [BuilderName.GovernmentIdAndDatabaseAndSelfie]: {
    title: 'Government ID (Front side only) and Database and Selfie',
    description:
      'Use this template as a starting point for Government ID front side, Database, and live Selfie identity verifications',
  },
  [BuilderName.GovernmentIdAndDocument]: {
    title: 'Government ID (Front side only) and Document',
    description:
      'Use this template as a starting point to verify a government-issued ID (front side only) along with a supporting document. Additional configuration required.',
  },
  [BuilderName.GovernmentIdAndFormAndSelfie]: {
    title: 'Government ID (Front side only) and pre-filled form and Selfie',
    description:
      'Use this template as a starting point for Government ID front side and a prefilled form and live Selfie identity verifications.',
  },
  [BuilderName.GovernmentIdAndSelfie]: {
    title: 'Government ID and Selfie',
    description:
      'Use this template as a starting point for combined Government ID and live Selfie identity verifications.',
  },
  [BuilderName.GovernmentIdAndSelfieAndDocument]: {
    title: 'Government ID, Selfie, and Document',
    description:
      'Use this template as a starting point to verify a government-issued ID, capture a live selfie, and validate a supporting document. Additional configuration required.',
  },
  [BuilderName.GovernmentIdAndSelfieAndProofOfAddress]: {
    title: 'Government ID, Selfie and Proof of Address',
    description:
      'Use this template to verify a government-issued ID, capture a live selfie, and validate proof of address documentation. No additional configuration required.',
  },
  [BuilderName.GovernmentIdFrontAndBack]: {
    title: 'Government ID (Both sides)',
    description:
      'Use this template as a starting point for Government ID front and back side identity verifications.',
  },
  [BuilderName.GovernmentIdWithAutoclassification]: {
    title: 'Government ID (Front side only)',
    description:
      'Use this template as a starting point for Government ID front side identity verifications. \
The submitted ID is auto-classified, users are not required to input their country and ID type before capture or upload.',
  },
  [BuilderName.GovernmentIdWithAutoclassificationAndSelfie]: {
    title: 'Government ID and Selfie',
    description:
      'Use this template as a starting point for combined Government ID and live Selfie identity verifications. \
The submitted ID is auto-classified, users are not required to input their country and ID type before capture or upload.',
  },
  [BuilderName.HealthInsuranceCard]: {
    title: 'Health Insurance Card (US Only)',
    description:
      'Use this template to verify health insurance cards, specifically for health insurance providers in the United States. No additional configuration required.',
  },
  [BuilderName.PhoneNumber]: {
    title: 'Phone Number',
    description: 'Use this template as a starting point for Phone Number verifications.',
  },
  [BuilderName.PhoneNumberAndDatabase]: {
    title: 'Phone Number and Database',
    description:
      'Use this template as a starting point for Phone Number and Database identity verifications.',
  },
  [BuilderName.PhoneNumberAndGovernmentIdAndSelfie]: {
    title: 'Phone Number and Government ID and Selfie',
    description:
      'Use this template as a starting point for Phone Number, Government ID, and live Selfie identity verifications.',
  },
  [BuilderName.Selfie]: {
    title: 'Selfie',
    description: 'Use this template as a starting point for live Selfie identity verifications.',
  },
  [BuilderName.VehicleInsurance]: {
    title: 'Vehicle Insurance (US Only)',
    description:
      'Use this template to verify vehicle insurance policies, limited to vehicle insurance providers in the United States. No additional configuration required.',
  },
};

export const AUTOCLASSIFICATION_BUILDER_SPECIFIC_TITLES: Partial<Record<BuilderName, string>> = {
  [BuilderName.GovernmentIdWithAutoclassification]:
    'Government ID (Front side with auto-classification)',
  [BuilderName.GovernmentIdWithAutoclassificationAndSelfie]:
    'Government ID (Front side with auto-classification) and Selfie',
};

/* modal body components */
const StyledModalContainer = styled(ModalContainer)`
  position: relative;
  height: clamp(700px, 85vh, 850px);
  width: clamp(980px, 90vw, 1100px);
  padding: 0;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-gap: ${Spacing[4]};
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: ${CARD_HEIGHT}px;
  height: 100%;
  overflow-y: scroll;

  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
  background-color: ${({ theme }) => theme.color.background.secondary};
  padding: ${Spacing[5]};
`;

const slideIn = keyframes`
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0);
  }
`;

const FullViewWrapper = styled.div`
  position: absolute;
  background: none;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const AnimatedWrapper = styled.div`
  position: relative;
  height: 100%;
  padding-top: ${HEADER_COLLAPSED}px;
  animation: ${slideIn} ${TransitionDuration.Fast} forwards;
`;

const NoResultsWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding-top: ${Spacing[8]};
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: inherit;
`;

/* modal header components */
const StyledModalHeader = styled.div<{ expanded: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
  height: ${({ expanded }) => (expanded ? HEADER_EXPANDED : HEADER_COLLAPSED)}px;
  transition: height ${TransitionDuration.Fast} linear;
  z-index: 1;
  padding: ${Spacing[5]};
  width: 100%;
`;

const HeaderTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledIconX = styled(XIcon).attrs(() => ({ size: 24 }))`
  cursor: pointer;
  margin-left: auto;
`;

const StyledIconNavBack = styled(ArrowLeftIcon).attrs(() => ({ size: 24 }))`
  cursor: pointer;
  flex: none;
  height: 20px;
  width: 20px;
  margin-right: ${Spacing[5]};
  svg {
    color: ${({ theme }) => theme.color.text.primary};
  }
`;

const SearchFilterRow = styled.div`
  display: grid;
  grid-template-columns: 2fr minmax(300px, 1fr);
  grid-gap: ${Spacing[4]};
  margin-top: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

interface CreateInquiryTemplateModalProps {
  inquiryTemplateBuilders: InquiryTemplateBuilder[];
  builderPreviews: BuilderPreviewMap;
  handleCloseModal: () => void;
  handleCreateInquiryTemplate: (builderName: string) => Promise<void>;
  handleLoadTemplateBuilderPreview: (builderName: string) => Promise<void>;
}

const CreateInquiryTemplateModal = ({
  inquiryTemplateBuilders,
  builderPreviews,
  handleCloseModal,
  handleCreateInquiryTemplate,
  handleLoadTemplateBuilderPreview,
}: CreateInquiryTemplateModalProps) => {
  const [selectedBuilder, setSelectedBuilder] = useState<InquiryTemplateBuilder | undefined>(
    undefined,
  );
  const [builderPreview, setBuilderPreview] = useState<BuilderPreviewSteps | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSetSearchTerm] = useDebounced(setSearchTerm, 500, {
    leading: false,
    trailing: true,
  });

  useEffect(() => {
    if (selectedBuilder != null) {
      if (builderPreviews[selectedBuilder.name] == null) {
        handleLoadTemplateBuilderPreview(selectedBuilder.name);
      } else {
        setBuilderPreview(builderPreviews[selectedBuilder.name]);
      }
    }
  }, [builderPreviews, selectedBuilder, handleLoadTemplateBuilderPreview, setBuilderPreview]);

  const isShowingAutoclasAndNonAutoclasTemplates = useMemo(() => {
    return (
      inquiryTemplateBuilders.some(
        (builder) => builder.name === BuilderName.GovernmentIdWithAutoclassification,
      ) && inquiryTemplateBuilders.some((builder) => builder.name === BuilderName.GovernmentId)
    );
  }, [inquiryTemplateBuilders]);

  const fuse = useMemo(() => {
    const buildersWithLabels = inquiryTemplateBuilders.map((builder) => ({
      ...builder,
      ...(isShowingAutoclasAndNonAutoclasTemplates
        ? {
            ...BUILDER_LABELS[builder.name],
            // overwrite title with the internal title if exists
            title:
              AUTOCLASSIFICATION_BUILDER_SPECIFIC_TITLES[builder.name] ||
              BUILDER_LABELS[builder.name]?.title,
          }
        : BUILDER_LABELS[builder.name]),
    }));
    return new Fuse(buildersWithLabels, {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      minMatchCharLength: 2,
      keys: [
        { name: 'title', weight: 0.7 },
        { name: 'description', weight: 0.3 },
      ],
    });
  }, [inquiryTemplateBuilders, isShowingAutoclasAndNonAutoclasTemplates]);

  const searchResults: Array<InquiryTemplateBuilder> | null = useMemo(
    () => (searchTerm !== '' ? fuse.search(searchTerm).map(({ item }) => item) : null),
    [fuse, searchTerm],
  );

  const [verificationFilters, setVerificationFilters] = useState<VerificationTemplate['type'][]>(
    [],
  );

  const cardsToShow = useMemo<React.ReactNode[]>(() => {
    const rows: ReactNode[] = [];
    inquiryTemplateBuilders.forEach((builder) => {
      // skip row if search in progress and row is not included in results
      if (searchResults != null && !searchResults.some((result) => result.name === builder.name)) {
        return;
      }

      const { name, 'verification-types': verificationTypes, enabled } = builder;
      const labels = BUILDER_LABELS[name];
      const title = isShowingAutoclasAndNonAutoclasTemplates
        ? AUTOCLASSIFICATION_BUILDER_SPECIFIC_TITLES[builder.name] || labels?.title
        : labels?.title;

      // early exit if client strings haven't been provided for the current builder. plan is to move these strings to the server
      // TODO (eric): update card display logic once strings have been migrated
      if (!labels || !title || !labels?.description) {
        return;
      }

      const builderCard = (
        <TemplateModuleCard
          size='md'
          id={`${name}-builder-card`}
          key={`${name}-builder-card`}
          title={title}
          description={labels.description}
          icon={<VerificationTypeIcon type={verificationTypes?.[0]} />}
          isLimited={!enabled}
          onClick={() => {
            setSelectedBuilder(builder);
          }}
        />
      );
      if (
        verificationFilters.length === 0 ||
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        verificationFilters.filter((typeFilter) => verificationTypes.includes(typeFilter)).length >
          0
      ) {
        rows.push(builderCard);
      }
    });

    return rows;
  }, [
    inquiryTemplateBuilders,
    searchResults,
    verificationFilters,
    isShowingAutoclasAndNonAutoclasTemplates,
  ]);

  const handleNavBack = () => {
    setSelectedBuilder(undefined);
    setBuilderPreview(undefined);
  };

  const heading = (() => {
    if (!selectedBuilder) {
      return 'Choose a template';
    }
    if (!isShowingAutoclasAndNonAutoclasTemplates) {
      return BUILDER_LABELS[selectedBuilder.name].title;
    }
    return (
      AUTOCLASSIFICATION_BUILDER_SPECIFIC_TITLES[selectedBuilder.name] ||
      BUILDER_LABELS[selectedBuilder.name].title
    );
  })();
  const builderVerificationTypes = inquiryTemplateBuilders.flatMap(
    ({ 'verification-types': verificationTypes }) => verificationTypes,
  );
  const detectedFilterOptions = TYPE_FILTER_OPTIONS.filter(({ value }) =>
    builderVerificationTypes.includes(value),
  );

  const verificationTypeFilterLabelTextMapper = useLabelTextMapper(
    detectedFilterOptions,
    'verification types',
    {
      emptyIsAll: true,
    },
  );

  return (
    <StyledModalContainer>
      <StyledModalHeader expanded={!selectedBuilder}>
        <HeaderTitleWrapper>
          {selectedBuilder && (
            <StyledIconNavBack id='create-template__nav-back' onClick={handleNavBack} />
          )}
          <Heading size='sm'>{heading}</Heading>
          <StyledIconX
            id='create-template__close-modal'
            data-test='create-template__close-modal'
            onClick={handleCloseModal}
          />
        </HeaderTitleWrapper>
        {!selectedBuilder && (
          <SearchFilterRow>
            <SearchInput
              autoFocus
              size='md'
              id='inquiry-templates-template__search'
              placeholder='Search'
              handleInput={(searchTerm) => debouncedSetSearchTerm(searchTerm)}
              value={searchTerm}
              title={null}
            />
            <MultiSelect
              size='md'
              value={verificationFilters}
              selectOptionValues={detectedFilterOptions}
              labelTextMapper={verificationTypeFilterLabelTextMapper}
              onChange={(filters) =>
                setVerificationFilters(filters as VerificationTemplate['type'][])
              }
            />
          </SearchFilterRow>
        )}
      </StyledModalHeader>
      {selectedBuilder ? (
        <FullViewWrapper>
          <AnimatedWrapper>
            <CreateInquiryTemplatePage
              builderName={selectedBuilder?.name}
              description={BUILDER_LABELS[selectedBuilder?.name].description}
              stepsPreview={builderPreview}
              enabled={selectedBuilder?.enabled}
              handleCreateInquiryTemplate={handleCreateInquiryTemplate}
            />
          </AnimatedWrapper>
        </FullViewWrapper>
      ) : (
        <SelectBuilderView cardsToShow={cardsToShow} />
      )}
    </StyledModalContainer>
  );
};

const SelectBuilderView = ({ cardsToShow }: { cardsToShow: ReactNode[] }) => {
  return cardsToShow.length > 0 ? (
    <CardsContainer>{cardsToShow}</CardsContainer>
  ) : (
    <NoResultsWrapper>
      <Paragraph size='md'>
        No matched results. Try changing your search and/or filter(s).
      </Paragraph>
    </NoResultsWrapper>
  );
};

export default CreateInquiryTemplateModal;
